import { AppBar, Toolbar, Box, Breadcrumbs, Typography, Link, IconButton, Tooltip, Avatar } from "@mui/material";
import { MenuRounded, CottageOutlined, LogoutRounded } from "@mui/icons-material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { DeviceTypeId } from "../../types";
import { authService } from "../../services/authService";

type BreadcrumbItem = { label: string; path?: string };

const routeBreadcrumbs: Record<string, BreadcrumbItem[]> = {
  "/": [{ label: "Anasayfa", path: "/" }, { label: "Tüm Cihazlar" }],
  "/Dashboard": [{ label: "Anasayfa", path: "/" }, { label: "Dashboard" }],
  "/Devices": [{ label: "Anasayfa", path: "/" }, { label: "Tüm Cihazlar" }],
  "/Devices?deviceTypeId=1": [{ label: "Anasayfa", path: "/" }, { label: "Sensör Cihazlar" }],
  "/Devices?deviceTypeId=2": [{ label: "Anasayfa", path: "/" }, { label: "Gateway Cihazlar" }],
  "/SensorParsers": [{ label: "Anasayfa", path: "/" }, { label: "Sensör Parserlar" }],
  "/GatewayParsers": [{ label: "Anasayfa", path: "/" }, { label: "Gateway Parserlar" }],
  "/ParserSimulate": [{ label: "Anasayfa", path: "/" }, { label: "Sensör Simüle" }],
  "/ParserDetail": [{ label: "Anasayfa", path: "/" }, { label: "Parse Yöntemleri", path: "/" }, { label: "Parser Ayarları" }],
  "/Settings": [{ label: "Anasayfa", path: "/" }, { label: "Ayarlar" }],
  "/Users": [{ label: "Anasayfa", path: "/" }, { label: "Yönetim" }, { label: "Kullanıcılar" }],
  "/Roles": [{ label: "Anasayfa", path: "/" }, { label: "Yönetim" }, { label: "Roller" }],
  "/Companies": [{ label: "Anasayfa", path: "/" }, { label: "Yönetim" }, { label: "Şirketler" }],
  "/Departments": [{ label: "Anasayfa", path: "/" }, { label: "Yönetim" }, { label: "Departmanlar" }],
  "/DeviceGroups": [{ label: "Anasayfa", path: "/" }, { label: "Yönetim" }, { label: "Cihaz Grup Ayarları" }],
  "/MyDeviceGroups": [{ label: "Anasayfa", path: "/" }, { label: "Cihazlar" }, { label: "Cihaz Gruplarım" }],
  "/Profile": [{ label: "Anasayfa", path: "/" }, { label: "Profilim" }],
};

type TopBarProps = {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
};

const getUserName = (): string => {
  try {
    const token = authService.getToken();
    if (!token) return "";
    const decoded = jwtDecode<{ name?: string }>(token);
    return decoded.name ?? "";
  } catch {
    return "";
  }
};

const TopBar = ({ sidebarOpen, onToggleSidebar }: TopBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userName = getUserName();

  const handleLogout = () => {
    authService.logout();
    navigate("/login", { replace: true });
  };

  const [searchParams] = useSearchParams();

  let crumbs: BreadcrumbItem[];
  if (location.pathname === "/ParserDetail") {
    const deviceTypeId = searchParams.get("deviceTypeId");
    if (deviceTypeId === String(DeviceTypeId.Sensor)) {
      crumbs = [{ label: "Anasayfa", path: "/" }, { label: "Sensör Parserlar", path: "/SensorParsers" }, { label: "Parser Ayarları" }];
    } else if (deviceTypeId === String(DeviceTypeId.Gateway)) {
      crumbs = [{ label: "Anasayfa", path: "/" }, { label: "Gateway Parserlar", path: "/GatewayParsers" }, { label: "Parser Ayarları" }];
    } else {
      crumbs = [{ label: "Anasayfa", path: "/" }, { label: "Parser Ayarları" }];
    }
  } else {
    const fullPath = location.pathname + location.search;
    crumbs = routeBreadcrumbs[fullPath] ?? routeBreadcrumbs[location.pathname] ?? [{ label: location.pathname }];
  }

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #e0e0e0",
        color: "#1a1a1a",
      }}
    >
      <Toolbar disableGutters sx={{ minHeight: 64 }}>
        {/* Sidebar genişliğiyle eşit kutu — hamburger her zaman sidebar'ın üstünde hizalı kalır */}
        <IconButton onClick={onToggleSidebar} sx={{ color: "#555", ml: 1, mr: 1 }}>
          <MenuRounded sx={{ fontSize: 28 }} />
        </IconButton>

        <Box
          component="img"
          src="/sensify-iot.png"
          alt="Logo"
          sx={{ height: 36, cursor: "pointer", mr: 2 }}
          onClick={() => navigate("/")}
        />

        <Breadcrumbs
          separator="›"
          sx={{
            ml: sidebarOpen ? { xs: 0, md: "32px" } : 0,
            transition: "margin-left 0.2s ease",
            "& .MuiBreadcrumbs-separator": { color: "var(--color-primary)", mx: 0.5, fontWeight: 700 },
          }}
        >
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            const isHome = index === 0;
            return isLast ? (
              <Typography key={index} fontSize={13} color="#888">
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                underline="hover"
                fontSize={13}
                sx={{ color: "#888", cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }}
                onClick={() => crumb.path && navigate(crumb.path)}
              >
                {isHome && <CottageOutlined sx={{ fontSize: 15, color: "var(--color-primary)" }} />}
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>

        <Box sx={{ flex: 1 }} />
        {userName && (
          <Box
            onClick={() => navigate("/Profile")}
            sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1, cursor: "pointer", "&:hover": { opacity: 0.8 } }}
          >
            <Avatar
              sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 700, bgcolor: "var(--color-primary)" }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography fontSize={13} fontWeight={500} color="#333" sx={{ display: { xs: "none", sm: "block" } }}>
              {userName}
            </Typography>
          </Box>
        )}
        <Tooltip title="Çıkış Yap">
          <IconButton onClick={handleLogout} sx={{ color: "#555", mr: 1 }}>
            <LogoutRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
